import { aiBaseModel, aiModelVariant } from '@seoplum/core'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Panel } from '@/components/panel'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { type CatalogueRow, useModels, useSetModelEnabled } from '@/lib/admin-ai'
import { formatCount, formatExact } from '@/lib/format'
import { formatMoneyPrecise } from '@/lib/money'
import { cn } from '@/lib/utils'
import { Age, FilterSelect, Segmented, SkeletonRows, Tag, Th } from '../../-parts'

type Allowed = 'all' | 'on' | 'off'

interface ModelSearch {
  q?: string
  provider?: string
  allowed?: Allowed
  retired?: boolean
}

export const Route = createFileRoute('/_app/admin/ai/_list/models')({
  validateSearch: (search: Record<string, unknown>): ModelSearch => {
    const next: ModelSearch = {}
    if (typeof search.q === 'string' && search.q.length > 0) next.q = search.q
    if (typeof search.provider === 'string' && search.provider.length > 0) {
      next.provider = search.provider
    }
    if (search.allowed === 'on' || search.allowed === 'off') next.allowed = search.allowed
    if (search.retired === true || search.retired === 'true') next.retired = true
    return next
  },
  component: ModelsPage,
})

const ALLOWED: readonly { value: Allowed; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'on', label: 'Allowed' },
  { value: 'off', label: 'Not allowed' },
]

/**
 * The catalogue is the vendor's, the allowlist is ours.
 *
 * Four hundred models arriving nightly must not become four hundred models we
 * are willing to pay for, so `enabled` is the one column the sync never writes
 * and the only control on this screen. A model that stopped being listed is
 * marked retired rather than deleted, because a run from March still has to
 * name what answered it.
 */
function ModelsPage() {
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const { q, provider, allowed = 'all', retired = false } = search
  const [draft, setDraft] = useState<string | null>(null)

  const { data, isPending } = useModels({
    search: q,
    enabled: allowed === 'all' ? undefined : allowed === 'on',
    includeRetired: retired,
  })

  const set = (next: Partial<ModelSearch>) =>
    navigate({ search: (prev) => ({ ...prev, ...next }), replace: true })

  const models = data?.models ?? []

  // The provider cut is applied here rather than sent, so the list of providers
  // stays the whole list instead of collapsing to the one already chosen.
  const providers = [...new Set(models.map((model) => model.provider))]
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({
      key: name,
      label: name,
      count: models.filter((model) => model.provider === name).length,
    }))

  const rows = provider ? models.filter((model) => model.provider === provider) : models
  const now = Date.now()

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form
          onSubmit={(event) => {
            event.preventDefault()
            set({ q: draft ? draft : undefined })
          }}
        >
          <Input
            value={draft ?? q ?? ''}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Search the catalogue"
            aria-label="Search the catalogue"
            className="h-9 w-[240px] font-mono text-[13px]"
          />
        </form>

        <FilterSelect
          all="All providers"
          options={providers}
          value={provider}
          onChange={(next) => set({ provider: next })}
        />

        <Segmented
          value={allowed}
          options={ALLOWED}
          onChange={(next) => set({ allowed: next === 'all' ? undefined : next })}
        />

        <button
          type="button"
          aria-pressed={retired}
          onClick={() => set({ retired: retired ? undefined : true })}
          className={cn(
            'inline-flex h-9 cursor-pointer items-center rounded-md border px-3 font-mono text-[12px] transition-colors',
            retired
              ? 'border-border-strong text-foreground'
              : 'border-border text-muted-foreground hover:text-foreground',
          )}
        >
          retired
        </button>
      </div>

      <Panel
        title="Catalogue"
        meta={data ? `${formatExact(data.enabled)} of ${formatExact(models.length)}` : undefined}
        flush
      >
        {isPending ? (
          <SkeletonRows rows={10} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="border-b border-border bg-secondary/30">
                  <Th align="left">Model</Th>
                  <Th align="left" className="w-[110px]">
                    Provider
                  </Th>
                  <Th className="w-[80px]">Context</Th>
                  <Th className="w-[80px]">Max out</Th>
                  <Th className="w-[90px]">In /M</Th>
                  <Th className="w-[90px]">Out /M</Th>
                  <Th className="w-[90px]">Cache /M</Th>
                  <Th className="w-[70px]">Runs</Th>
                  <Th className="w-[80px]">Synced</Th>
                  <Th className="w-[70px]">Allowed</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((model) => (
                  <Row key={model.id} model={model} now={now} />
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-3 py-6 text-center text-muted-foreground">
                      Nothing in the catalogue matches that.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  )
}

function Row({ model, now }: { model: CatalogueRow; now: number }) {
  const toggle = useSetModelEnabled()
  const variant = aiModelVariant(model.id)

  return (
    <tr className={cn('border-b border-border last:border-b-0', model.retiredAt && 'opacity-60')}>
      <td className="py-2 pr-3 pl-3">
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="font-mono text-[12px]">{aiBaseModel(model.id)}</span>
          {/* The same weights, queued behind everyone else's, at half the
              price. Legible as a tier rather than as a second model. */}
          {variant && <Tag tone="plain">{variant}</Tag>}
          {model.retiredAt && <Tag tone="warn">retired</Tag>}
        </span>
        <span className="block max-w-[320px] truncate text-[11.5px] text-muted-foreground">
          {model.name}
        </span>
      </td>

      <td className="px-3 py-2 font-mono text-[12px] text-muted-foreground">{model.provider}</td>

      <td className="px-3 py-2 text-right font-mono text-[12px] tabular">
        {model.contextLength === null ? '' : formatCount(model.contextLength)}
      </td>
      <td className="px-3 py-2 text-right font-mono text-[12px] tabular">
        {model.maxOutputTokens === null ? '' : formatCount(model.maxOutputTokens)}
      </td>

      <Price value={model.inputPerMillion} />
      <Price value={model.outputPerMillion} />
      {/* Read then write, the two halves of a cached prompt's bill. */}
      <td className="px-3 py-2 text-right font-mono text-[11.5px] text-muted-foreground tabular">
        {model.cacheReadPerMillion === null && model.cacheWritePerMillion === null
          ? ''
          : `${formatMoneyPrecise(model.cacheReadPerMillion ?? 0)} / ${formatMoneyPrecise(
              model.cacheWritePerMillion ?? 0,
            )}`}
      </td>

      <td className="px-3 py-2 text-right font-mono text-[12px] tabular">
        {model.runs === 0 ? '' : formatExact(model.runs)}
      </td>

      <td className="px-3 py-2 text-right">
        <Age at={model.syncedAt} now={now} />
      </td>

      <td className="px-3 py-2 text-right">
        <Button
          size="xs"
          variant={model.enabled ? 'default' : 'outline'}
          disabled={toggle.isPending}
          onClick={() => toggle.mutate({ modelId: model.id, enabled: !model.enabled })}
          className="w-11 font-mono"
        >
          {model.enabled ? 'on' : 'off'}
        </Button>
      </td>
    </tr>
  )
}

function Price({ value }: { value: number | null }) {
  return (
    <td className="px-3 py-2 text-right font-mono text-[12px] tabular">
      {value === null ? '' : formatMoneyPrecise(value)}
    </td>
  )
}
